import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { cellRefMatches } from './types';
import type { ReviewStore } from './types';

/** Return a copy of `set` with `value` toggled in: removed if present, else added. */
const withToggled = (set: Set<string>, value: string): Set<string> => {
  const next = new Set(set);
  if (!next.delete(value)) {
    next.add(value);
  }
  return next;
};

const useReviewStore = create<ReviewStore>()(
  devtools(
    set => ({
      reviewItems: [],
      selectedCardIds: new Set<string>(),
      filterSecurities: new Set<string>(),
      filterMetrics: new Set<string>(),
      hoveredReviewCell: null,
      selectedReviewCell: null,
      drawerOpen: false,
      showAiColumnControls: false,

      setReviewItems: items => set(() => ({ reviewItems: items }), false, 'setReviewItems'),

      toggleCardSelection: id =>
        set(state => ({ selectedCardIds: withToggled(state.selectedCardIds, id) }), false, 'toggleCardSelection'),

      selectCards: ids => set(() => ({ selectedCardIds: new Set(ids) }), false, 'selectCards'),

      clearSelection: () => set(() => ({ selectedCardIds: new Set<string>() }), false, 'clearSelection'),

      toggleFilterSecurity: label =>
        set(state => ({ filterSecurities: withToggled(state.filterSecurities, label) }), false, 'toggleFilterSecurity'),

      toggleFilterMetric: label =>
        set(state => ({ filterMetrics: withToggled(state.filterMetrics, label) }), false, 'toggleFilterMetric'),

      clearReviewFilter: () =>
        set(() => ({ filterSecurities: new Set<string>(), filterMetrics: new Set<string>() }), false, 'clearReviewFilter'),

      removeReviewItem: id =>
        set(
          state => {
            const next = new Set(state.selectedCardIds);
            next.delete(id);
            return {
              reviewItems: state.reviewItems.filter(i => i.id !== id),
              selectedCardIds: next,
            };
          },
          false,
          'removeReviewItem'
        ),

      setReviewItemStatus: (id, status) =>
        set(
          state => {
            const next = new Set(state.selectedCardIds);
            next.delete(id);
            return {
              reviewItems: state.reviewItems.map(i => (i.id === id ? { ...i, status } : i)),
              selectedCardIds: next,
            };
          },
          false,
          'setReviewItemStatus'
        ),

      setHoveredReviewCell: cell => set(() => ({ hoveredReviewCell: cell }), false, 'setHoveredReviewCell'),

      setSelectedReviewCell: cell => set(() => ({ selectedReviewCell: cell }), false, 'setSelectedReviewCell'),

      setDrawerOpen: open => set(() => ({ drawerOpen: open }), false, 'setDrawerOpen'),

      setShowAiColumnControls: show =>
        set(() => ({ showAiColumnControls: show }), false, 'setShowAiColumnControls'),
    }),
    { name: 'ReviewStore' }
  )
);

/**
 * Subscribe to whether one specific cell should reveal its AI merge text / outline:
 * true when (securityId, fieldAlias) matches the hovered OR the selected review cell.
 * The selector returns a plain boolean, so a cell's viewer only re-renders when its
 * own match flips - hovering a card does not re-render the whole grid.
 */
export const useCellShowsMerge = (securityId?: number, fieldAlias?: string): boolean =>
  useReviewStore(s => {
    if (securityId == null || !fieldAlias) {
      return false;
    }
    const target = { securityId, fieldAlias };
    return cellRefMatches(s.hoveredReviewCell, target) || cellRefMatches(s.selectedReviewCell, target);
  });

/**
 * Number of items still PENDING review. The aggregator now emits accepted and
 * dismissed items too (so reviewed cards render at the bottom), so the "needs
 * review" count must filter to pending - not the full list length.
 */
export const usePendingReviewCount = (): number =>
  useReviewStore(s => s.reviewItems.reduce((n, i) => (i.status === 'pending' ? n + 1 : n), 0));

/**
 * Whether the review drawer is open. Pending AI cells subscribe to this so they paint
 * AI-purple by default while the drawer is open and revert when it closes (SCAL-9150).
 * Returns a plain boolean, so a cell's viewer re-renders only when the flag flips.
 */
export const useReviewDrawerOpen = (): boolean => useReviewStore(s => s.drawerOpen);

export default useReviewStore;
